import moment from "moment";
import {
  FORMULA_FUNCTION_NAMES,
  FORMULA_KEYWORDS,
  calculateFormula,
  checkFormulaSyntax,
} from "services/utils/formulaUtils";

// Form rules, calculation previews and derived values for Salary
// Components. The screen's records come from the API (salaryComponentApi.js).
// The sample data further down is still used by Salary Templates.

export const PREVIEW_GROSS = 30000;

export const CATEGORIES = [
  "Earnings",
  "Additional Earnings",
  "Deductions",
  "Additional Deductions",
  "Employer Contribution",
  "Reimbursement",
  "Calculation",
];

// Fallback form categories, used only when get-component-choices sends
// none (the form otherwise offers exactly the server's list).
export const USER_CATEGORIES = [
  "Earnings",
  "Additional Earnings",
  "Deductions",
  "Additional Deductions",
  "Employer Contribution",
  "Reimbursement",
];

export const CATEGORY_TONES = {
  Earnings: "green",
  "Additional Earnings": "blue",
  Deductions: "red",
  "Additional Deductions": "purple",
  "Employer Contribution": "amber",
  Reimbursement: "blue",
  Calculation: "purple",
};

// Status chip colours for the shared status chip (as on the Pay Inputs
// lists), in the colours of the matching list cards.
export const COMPONENT_STATUS_COLORS = { Used: "#F0954A", Unused: "#12B76A", System: "#475569", Inactive: "#6B7280" };

export const METHODS = ["Fixed Amount", "Percentage", "Formula", "Pay Input", "Statutory"];
export const EDITABLE_METHODS = [
  { value: "Fixed Amount", label: "Fixed Amount", helper: "A fixed value per pay period" },
  { value: "Percentage", label: "Percentage", helper: "A share of a base" },
  { value: "Formula", label: "Formula", helper: "Codes, operators, functions" },
];

export const FORMULA_FUNCTIONS = ["MIN", "MAX", "ROUND", "IF"];

const DATE_FORMAT = "DD MMM YYYY";
export const formatDay = (value) => (value ? moment(value).format(DATE_FORMAT) : "–");

export const formatRupees = (value, digits = 0) =>
  value == null || Number.isNaN(Number(value))
    ? "–"
    : `₹${Number(value).toLocaleString("en-IN", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}`;

export const versionLabel = (number) => `V${number}`;
export const currentVersion = (component) => component.versions[component.versions.length - 1];

// The fields that make up a version. Changing any of them on a component
// that a template uses saves a new version; the rest update in place.
export const VERSIONED_FIELDS = [
  { key: "category", label: "Category" },
  { key: "method", label: "Method" },
  { key: "amount", label: "Amount" },
  { key: "percentage", label: "Percentage" },
  { key: "base", label: "Of" },
  { key: "formula", label: "Formula" },
  { key: "prorate", label: "Prorate for paid days" },
];

// A percentage can be of several codes together (production's
// "Percentage Based On", e.g. BASIC + DA). The screens keep the base as one
// text value ("BASIC + DA"); the API gets and sends a list of codes.
export const baseCodes = (base) =>
  [...new Set((Array.isArray(base) ? base : String(base ?? "").split(/[+,]/)).map((code) => String(code ?? "").trim()).filter(Boolean))];
export const joinBase = (base) => baseCodes(base).join(" + ");

// percent_base_code for the API. The server reads one code as a plain
// string ("GROSS"); a list (["GROSS"]) reaches it as the text "['GROSS']",
// which matches no code. So one code goes as a string, and a list is sent
// only when several codes are picked (needs backend support).
export const baseForApi = (base) => {
  const codes = baseCodes(base);
  if (codes.length > 1) return codes;
  return codes[0] || "GROSS";
};

// The total of the base codes, or null while any of them isn't worked out yet.
export const baseAmount = (base, values) => {
  const codes = baseCodes(base);
  if (!codes.length || codes.some((code) => values[code] == null)) return null;
  return codes.reduce((sum, code) => sum + Number(values[code]), 0);
};

export const describeCalculation = (version) => {
  if (!version) return "–";
  switch (version.method) {
    case "Fixed Amount":
      return formatRupees(version.amount);
    case "Percentage":
      return `${version.percentage}% of ${version.base}`;
    case "Formula":
      return version.formula;
    case "Pay Input":
      return "From Pay Inputs";
    case "Statutory":
      return "Statutory engine";
    default:
      return "–";
  }
};

// The calculation a version describes, as the field the diff tables show.
export const versionFieldValue = (version, key) => {
  if (!version) return "–";
  if (key === "prorate") return version.method === "Fixed Amount" ? (version.prorate ? "Yes" : "No") : "–";
  if (key === "amount") return version.method === "Fixed Amount" ? formatRupees(version.amount) : "–";
  if (key === "percentage") return version.method === "Percentage" ? `${version.percentage}%` : "–";
  if (key === "base") return version.method === "Percentage" ? version.base : "–";
  if (key === "formula") return version.method === "Formula" ? version.formula : "–";
  return version[key] ?? "–";
};

export const changedVersionFields = (before, after) =>
  VERSIONED_FIELDS.filter(({ key }) => versionFieldValue(before, key) !== versionFieldValue(after, key));

// ---------- Formula evaluation ----------
// Formulas are read by the shared reader in services/utils/formulaUtils.js:
// + - * /, brackets, %, comparisons, AND / OR / NOT, MIN, MAX, ROUND(x[, n]),
// IF, ABS, FLOOR, CEIL.

// Words a code can never be: function names, AND / OR / NOT, GROSS / CTC.
export const RESERVED_CODES = [...FORMULA_FUNCTION_NAMES, ...FORMULA_KEYWORDS, "GROSS", "EGS", "CTC"];

// Component codes a formula reads (functions and keywords left out).
export const codesIn = (formula) => {
  const syntax = checkFormulaSyntax(formula);
  if (syntax.valid) return syntax.codes;
  return [...new Set(String(formula || "").toUpperCase().match(/[A-Z_][A-Z0-9_]*/g) || [])].filter(
    (token) => !FORMULA_FUNCTION_NAMES.includes(token) && !FORMULA_KEYWORDS.includes(token),
  );
};

export const evaluateFormula = (formula, values) => calculateFormula(formula, values);

// Codes a version reads: a formula's codes, or a percentage's base.
const versionCodes = (version) => {
  if (!version) return [];
  if (version.method === "Formula") return codesIn(version.formula);
  if (version.method === "Percentage") return baseCodes(version.base);
  return [];
};

// The chain back to ownCode when the draft reads (through other components)
// the component itself, e.g. ["HRA", "SPL", "HRA"]; null when there's none.
export const findCircularPath = (ownCode, draftCodes, components) => {
  if (!ownCode) return null;
  const deps = {};
  components.forEach((item) => {
    if (item.active && item.code !== ownCode) deps[item.code] = versionCodes(currentVersion(item));
  });
  deps[ownCode] = draftCodes;
  const seen = new Set();
  const walk = (code, path) => {
    for (const next of deps[code] || []) {
      if (next === ownCode) return [...path, next];
      if (seen.has(next)) continue;
      seen.add(next);
      const found = walk(next, [...path, next]);
      if (found) return found;
    }
    return null;
  };
  return walk(ownCode, [ownCode]);
};

// Checks a formula: syntax first, then the codes it uses. Returns { valid,
// message, codes }. context (Salary Components): { ownCode, components } adds
// the server's rules: no CTC or statutory codes, no circular reference.
export const checkFormula = (formula, allowedCodes, context) => {
  const text = String(formula || "").trim();
  if (!text) return { valid: false, message: "Enter a formula", codes: [] };
  const syntax = checkFormulaSyntax(text);
  if (!syntax.valid) return { valid: false, message: syntax.message, codes: [] };
  const codes = syntax.codes;
  if (codes.includes("CTC")) return { valid: false, message: "CTC can't be used in a formula", codes };
  if (context?.components) {
    const statutory = codes.filter((code) =>
      context.components.some((item) => item.code === code && currentVersion(item)?.method === "Statutory"),
    );
    if (statutory.length) return { valid: false, message: `Statutory codes can't be used: ${statutory.join(", ")}`, codes };
  }
  const unknown = codes.filter((code) => !allowedCodes.includes(code));
  if (unknown.length) return { valid: false, message: `Unknown code: ${unknown.join(", ")}`, codes };
  if (context?.components) {
    const loop = findCircularPath(context.ownCode, codes, context.components);
    if (loop) return { valid: false, message: `Circular reference: ${loop.join(" → ")}`, codes };
  }
  return { valid: true, message: codes.length ? `Valid formula · uses ${codes.join(", ")}` : "Valid formula", codes };
};

// ---------- Values at a sample GROSS ----------

export const amountFor = (version, values) => {
  switch (version.method) {
    case "Fixed Amount":
      return Number(version.amount) || 0;
    case "Percentage": {
      const base = baseAmount(version.base, values);
      return base == null ? null : (base * Number(version.percentage)) / 100;
    }
    case "Formula":
      return codesIn(version.formula).every((code) => values[code] != null)
        ? evaluateFormula(version.formula, values)
        : null;
    default:
      return null;
  }
};

// Code -> amount at the given GROSS for every active component worked out
// here (Fixed / Percentage / Formula), earnings and deductions alike.
// Components can depend on each other (HRA on BASIC, SPL on all), so this
// repeats until nothing new resolves.
export const resolveEarningValues = (components, gross = PREVIEW_GROSS) => {
  const values = { GROSS: gross, EGS: gross };
  const calculated = components.filter(
    (item) => item.active && ["Fixed Amount", "Percentage", "Formula"].includes(currentVersion(item)?.method),
  );
  for (let pass = 0; pass < calculated.length + 1; pass += 1) {
    let changed = false;
    calculated.forEach((item) => {
      if (values[item.code] != null) return;
      const value = amountFor(currentVersion(item), values);
      if (value != null) {
        values[item.code] = Math.round(value * 100) / 100;
        changed = true;
      }
    });
    if (!changed) break;
  }
  return values;
};

// Preview for one (possibly unsaved) version at the given GROSS: the amount
// plus the lines that explain it.
export const previewVersion = (version, components, gross = PREVIEW_GROSS, ownCode) => {
  const values = resolveEarningValues(
    components.filter((item) => item.code !== ownCode),
    gross,
  );
  const amount = amountFor(version, values);
  const lines = [];
  if (version.method === "Percentage") {
    baseCodes(version.base)
      .filter((code) => code !== "GROSS" && values[code] != null)
      .forEach((code) => lines.push({ label: code, value: values[code] }));
  }
  if (version.method === "Formula") {
    codesIn(version.formula)
      .filter((code) => code !== "GROSS" && code !== "EGS")
      .forEach((code) => lines.push({ label: code, value: values[code] }));
  }
  return { amount: amount == null ? null : Math.round(amount * 100) / 100, lines, values };
};

// ---------- Sample data ----------

const TEMPLATES = {
  staff: { name: "Staff — Monthly", pay_basis: "Monthly", template_version: 4, employees: 112 },
  operators: { name: "Operators — Monthly", pay_basis: "Monthly", template_version: 2, employees: 64 },
  shift: { name: "Shift Workers — Daily", pay_basis: "Daily", template_version: 2, employees: 18 },
  trainees: { name: "Trainees — Monthly", pay_basis: "Monthly", template_version: 1, employees: 0 },
  supervisors: { name: "Supervisors — Monthly", pay_basis: "Monthly", template_version: 3, employees: 38 },
  drivers: { name: "Drivers — Daily", pay_basis: "Daily", template_version: 1, employees: 16 },
};

const FIRST_NAMES = [
  "Arun Kumar", "Meena Lakshmi", "Karthik Raja", "Divya S", "Murugan P", "Sathish V",
  "Revathi N", "Ganesh M", "Priya R", "Vignesh K", "Anitha J", "Suresh B", "Kavya T",
  "Rajesh A", "Deepa M", "Hari Prasad", "Lakshmi V", "Manoj C", "Nandhini G", "Senthil R",
];
const DEPARTMENTS = ["Production", "Quality", "Maintenance", "Dyeing", "Stores", "Logistics", "Accounts"];
const EFFECTIVE_DATES = ["2026-04-01", "2026-06-01", "2026-07-15", "2025-04-01", "2025-08-01"];

// usage: [templateKey, versionNumber, override?] — override is the template's
// own calculation when it differs from the component's version.
const usageFor = (usage) =>
  usage.map(([key, version, override]) => ({
    key,
    ...TEMPLATES[key],
    uses_version: version,
    calc_override: override || null,
  }));

const buildEmployees = (component) => {
  const rows = [];
  let serial = 0;
  component.templates.forEach((template) => {
    const version = component.versions.find((item) => item.version === template.uses_version) || currentVersion(component);
    for (let i = 0; i < template.employees; i += 1) {
      serial += 1;
      const seed = (component.id * 31 + serial * 7) % 997;
      const daily = template.pay_basis === "Daily";
      const gross = daily ? 700 + (seed % 5) * 50 : 24000 + (seed % 17) * 1000;
      const customized = serial % 27 === 4;
      const calcText = customized
        ? `Fixed ${formatRupees(Math.round(gross * 0.4))}`
        : template.calc_override || describeCalculation(version);
      let amount;
      if (customized) amount = Math.round(gross * 0.4);
      else if (template.calc_override) {
        const pct = Number(String(template.calc_override).match(/^(\d+(?:\.\d+)?)%/)?.[1]);
        amount = Number.isFinite(pct) ? (gross * pct) / 100 : null;
      } else {
        // Rough split of this employee's GROSS so formulas over other
        // earnings have something to read.
        amount = amountFor(version, {
          GROSS: gross, EGS: gross, BASIC: gross * 0.5, HRA: gross * 0.2, DA: gross * 0.05, CONV: daily ? 0 : 1600, FOOD: 50,
        });
      }
      rows.push({
        id: `${component.id}-${serial}`,
        name: FIRST_NAMES[(serial - 1) % FIRST_NAMES.length],
        employee_code: `EMP-${String(1000 + ((seed * 13) % 900)).padStart(4, "0")}`,
        department: DEPARTMENTS[seed % DEPARTMENTS.length],
        template: template.name,
        customized,
        version: template.uses_version,
        calculation: calcText,
        amount: amount == null ? null : Math.round(amount),
        per: daily ? "day" : "month",
        effective_from: EFFECTIVE_DATES[(seed + template.uses_version) % EFFECTIVE_DATES.length],
      });
    }
  });
  return rows;
};

const v = (version, fields, created_at, created_by, note) => ({
  version,
  prorate: true,
  amount: "",
  percentage: "",
  base: "GROSS",
  formula: "",
  ...fields,
  created_at,
  created_by,
  note,
});

const raw = [
  {
    name: "Basic Salary", code: "BASIC", description: "Basic pay as per contract", payslip_name: "Basic Pay",
    category: "Earnings",
    versions: [
      v(1, { method: "Fixed Amount", amount: 12000 }, "2024-04-01", "Sadheesh", "Component created."),
      v(2, { method: "Percentage", percentage: 40 }, "2025-04-10", "Sadheesh", "Changed from a fixed amount to a share of GROSS."),
      v(3, { method: "Percentage", percentage: 50 }, "2026-09-02", "Priya R (HR Manager)", "Basic raised to 50% of GROSS to align with the new wage definition."),
    ],
    usage: [["staff", 3], ["operators", 3, "45% of GROSS"], ["shift", 3], ["trainees", 3], ["supervisors", 2], ["drivers", 2]],
  },
  {
    name: "House Rent Allowance", code: "HRA", description: "HRA as a share of basic",
    category: "Earnings",
    versions: [v(1, { method: "Percentage", percentage: 40, base: "BASIC" }, "2024-04-01", "Sadheesh", "Component created.")],
    usage: [["staff", 1], ["operators", 1], ["supervisors", 1]],
  },
  {
    name: "Dearness Allowance", code: "DA", description: "Cost-of-living allowance",
    category: "Earnings",
    versions: [v(1, { method: "Percentage", percentage: 10, base: "BASIC" }, "2024-04-01", "Sadheesh", "Component created.")],
    usage: [["staff", 1], ["operators", 1]],
  },
  {
    name: "Conveyance Allowance", code: "CONV", description: "Travel allowance",
    category: "Earnings",
    versions: [v(1, { method: "Fixed Amount", amount: 1600 }, "2024-04-01", "Sadheesh", "Component created.")],
    usage: [["staff", 1], ["operators", 1], ["supervisors", 1], ["drivers", 1]],
  },
  {
    name: "Food Allowance", code: "FOOD", description: "Allowance for shift staff",
    category: "Earnings",
    versions: [v(1, { method: "Fixed Amount", amount: 50, prorate: false }, "2024-06-12", "Sadheesh", "Component created.")],
    usage: [["shift", 1], ["drivers", 1]],
  },
  {
    name: "Special Allowance", code: "SPL", description: "Balances earnings to GROSS", balancing: true,
    category: "Earnings",
    versions: [
      v(1, { method: "Formula", formula: "GROSS - BASIC" }, "2024-04-01", "Sadheesh", "Component created."),
      v(2, { method: "Formula", formula: "GROSS - (BASIC + HRA)" }, "2024-09-18", "Sadheesh", "HRA taken out of the balance."),
      v(3, { method: "Formula", formula: "GROSS - (BASIC + HRA + DA + CONV)" }, "2025-04-10", "Sadheesh", "DA and conveyance taken out of the balance."),
    ],
    usage: [["staff", 3], ["operators", 3], ["supervisors", 3]],
  },
  {
    name: "Medical Allowance", code: "MED", description: "Fixed medical support",
    category: "Earnings",
    versions: [v(1, { method: "Fixed Amount", amount: 1250 }, "2026-08-20", "Priya R (HR Manager)", "Component created.")],
    usage: [],
  },
  {
    name: "Night Shift Allowance", code: "NSA", description: "Paid to staff on night shifts, capped at ₹2,000",
    category: "Earnings",
    versions: [v(1, { method: "Formula", formula: "MIN(BASIC * 10%, 2000)", prorate: false }, "2026-10-02", "Priya R (HR Manager)", "Component created.")],
    usage: [],
  },
  { name: "Overtime", code: "OT", description: "Amount from Overtime pay inputs", category: "Additional Earnings", system: true, method: "Pay Input" },
  { name: "Incentive", code: "INCENT", description: "Amount from Incentive pay inputs", category: "Additional Earnings", system: true, method: "Pay Input" },
  { name: "Bonus", code: "BONUS", description: "Approved bonus pay inputs", category: "Additional Earnings", system: true, method: "Pay Input" },
  {
    name: "Canteen Deduction", code: "CANT", description: "Monthly canteen charge",
    category: "Deductions",
    versions: [v(1, { method: "Fixed Amount", amount: 500, prorate: false }, "2024-05-02", "Sadheesh", "Component created.")],
    usage: [["staff", 1], ["operators", 1]],
  },
  { name: "PF (Employee)", code: "PF_EMP", description: "Employee provident fund", category: "Deductions", system: true, method: "Statutory" },
  { name: "ESI (Employee)", code: "ESI_EMP", description: "Employee state insurance", category: "Deductions", system: true, method: "Statutory" },
  { name: "Professional Tax", code: "PT", description: "State professional tax", category: "Deductions", system: true, method: "Statutory" },
  { name: "Penalty", code: "PENALTY", description: "Approved penalty pay inputs", category: "Additional Deductions", system: true, method: "Pay Input" },
  { name: "Loan Recovery", code: "LOAN_REC", description: "Loan and advance instalments", category: "Additional Deductions", system: true, method: "Pay Input" },
  { name: "PF (Employer)", code: "PF_ER", description: "Employer provident fund", category: "Employer Contribution", system: true, method: "Statutory", show_in_payslip: false },
  { name: "ESI (Employer)", code: "ESI_ER", description: "Employer state insurance", category: "Employer Contribution", system: true, method: "Statutory", show_in_payslip: false },
];

export const withUsage = (component) => ({ ...component, employees: buildEmployees(component) });

export const buildSampleComponents = () =>
  raw.map((item, index) => {
    const versions =
      item.versions ||
      [v(1, { method: item.method, prorate: false }, "2024-04-01", "System", "Added by payroll setup.")];
    const created = versions[0];
    const component = {
      id: index + 1,
      name: item.name,
      code: item.code,
      description: item.description,
      payslip_name: item.payslip_name || "",
      category: item.category,
      show_in_payslip: item.show_in_payslip ?? true,
      system: Boolean(item.system),
      balancing: Boolean(item.balancing),
      active: true,
      deactivation: null,
      versions: versions.map((version) => ({ ...version, category: item.category })),
      templates: usageFor(item.usage || []),
      activity: [{ title: `Created as V1`, at: created.created_at, by: created.created_by }],
    };
    return withUsage(component);
  });

// ---------- Derived values ----------

// Templates / employees using a component: the server's counts, else the
// loaded lists.
export const templateCount = (component) => component.template_count ?? component.templates.length;

export const employeeCount = (component) =>
  component.employee_count ??
  (component.details_loaded
    ? component.employees.length
    : component.templates.reduce((total, template) => total + template.employees, 0));

// Used / Unused / System / Inactive: the server's status when it sends one.
export const componentStatus = (component) => {
  if (!component.active) return "Inactive";
  if (["Used", "Unused", "System"].includes(component.status)) return component.status;
  if (component.system) return "System";
  return templateCount(component) ? "Used" : "Unused";
};

export const lastChanged = (component) => {
  const version = currentVersion(component);
  const latestActivity = component.activity?.[0];
  return latestActivity && moment(latestActivity.at).isAfter(version.created_at)
    ? { at: latestActivity.at, version: null }
    : { at: version.created_at, version: version.version };
};

// The flat row the list table and CustomFilter work on.
export const toListRow = (component) => {
  const version = currentVersion(component);
  return {
    id: component.id,
    name: component.name,
    description: component.description,
    code: component.code,
    category: component.category,
    method: version.method,
    default_value: describeCalculation(version),
    payslip: component.show_in_payslip ? "Yes" : "No",
    version: versionLabel(version.version),
    status: componentStatus(component),
    system: component.system,
    balancing: component.balancing,
    active: component.active,
  };
};

export const emptyComponentForm = () => ({
  name: "",
  code: "",
  category: "Earnings",
  show_in_payslip: true,
  payslip_name: "",
  description: "",
  method: "Fixed Amount",
  amount: "",
  percentage: "",
  base: "GROSS",
  formula: "",
  prorate: true,
  note: "",
});

export const componentToForm = (component) => {
  const version = currentVersion(component);
  return {
    name: component.name,
    code: component.code,
    category: component.category,
    show_in_payslip: component.show_in_payslip,
    payslip_name: component.payslip_name,
    description: component.description,
    method: version.method,
    amount: version.amount === "" ? "" : String(version.amount),
    percentage: version.percentage === "" ? "" : String(version.percentage),
    base: joinBase(version.base) || "GROSS",
    formula: version.formula,
    prorate: version.prorate,
    note: "",
  };
};

// The version a form describes (unsaved).
export const formToVersion = (form) => ({
  category: form.category,
  method: form.method,
  amount: form.method === "Fixed Amount" ? Number(form.amount) : "",
  percentage: form.method === "Percentage" ? Number(form.percentage) : "",
  base: form.method === "Percentage" ? joinBase(form.base) : "GROSS",
  formula: form.method === "Formula" ? form.formula.trim() : "",
  // Prorate applies to Fixed Amount only.
  prorate: form.method === "Fixed Amount" && Boolean(form.prorate),
});

// Codes a formula may use (server rule): GROSS plus the company's other
// active codes, except statutory ones (and CTC, which isn't a component).
export const formulaCodes = (components, ownCode) => [
  ...components
    .filter((item) => item.active && item.code !== ownCode && currentVersion(item)?.method !== "Statutory")
    .map((item) => item.code),
  "GROSS",
];

// Percentage "Of" choices when the server's list isn't available (server
// rule): GROSS or another active Earnings code.
export const percentBaseCodes = (components, ownCode) => [
  ...components
    .filter((item) => item.category === "Earnings" && item.active && item.code !== ownCode)
    .map((item) => item.code),
  "GROSS",
];

export const validateComponentForm = (form, { components, editing, needsNote }) => {
  const errors = {};
  const name = form.name.trim();
  const code = form.code.trim().toUpperCase();
  if (!name) errors.name = "Enter a component name";
  else if (components.some((item) => item.id !== editing?.id && item.name.toLowerCase() === name.toLowerCase())) {
    errors.name = "A component with this name already exists";
  }
  if (!editing) {
    if (!code) errors.code = "Enter a code";
    else if (!/^[A-Z][A-Z0-9_]*$/.test(code)) errors.code = "Uppercase letters, numbers and _ only, starting with a letter";
    else if (RESERVED_CODES.includes(code)) errors.code = `${code} is reserved and can't be used as a code`;
    else if (components.some((item) => item.code === code)) errors.code = "This code is already used";
  }
  if (!form.category) errors.category = "Select a category";
  // A system component may arrive as "Statutory" / "Pay Input"; editing it
  // needs one of the form's methods.
  if (!EDITABLE_METHODS.some((item) => item.value === form.method)) errors.method = "Select a calculation method";
  if (form.method === "Fixed Amount" && !(Number(form.amount) > 0)) errors.amount = "Enter an amount above 0";
  if (form.method === "Percentage") {
    const pct = Number(form.percentage);
    if (!(pct > 0 && pct <= 100)) errors.percentage = "Enter a percentage between 0 and 100";
    if (!baseCodes(form.base).length) errors.base = "Select a base";
  }
  if (form.method === "Formula") {
    const ownCode = editing?.code || code;
    const check = checkFormula(form.formula, formulaCodes(components, ownCode), { ownCode, components });
    if (!check.valid) errors.formula = check.message;
  }
  if (needsNote && !form.note.trim()) errors.note = "Enter a change note";
  return errors;
};
